import React from "react";

const Bar = ({ className = "" }) => (
  <div aria-hidden="true" className={`rounded-full ${className}`} />
);

export const BrowserFrame = ({ url, children, label }) => (
  <div
    className="flex h-full flex-col overflow-hidden rounded-xl border border-white/15 bg-[#0B0B14] shadow-2xl"
    role="img"
    aria-label={label}
  >
    <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
      <div className="flex gap-1" aria-hidden="true">
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
      </div>
      <div className="ml-1 flex h-4 flex-1 items-center rounded-full bg-white/5 px-2.5">
        <span className="text-[7px] tracking-[0.18em] text-white/40">{url}</span>
      </div>
    </div>
    <div className="relative min-h-0 flex-1 bg-[#07070E]">{children}</div>
  </div>
);

export const ChaleScreen = () => (
  <div className="relative h-full overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-[#0B1530] via-[#0E1B3E] to-[#07070E]" />
    <div className="absolute right-6 top-4 h-9 w-9 rounded-full bg-[#00E5FF]/25 blur-md" />
    <div
      className="absolute bottom-10 h-[45%] w-full bg-[#0D1226]"
      style={{ clipPath: "polygon(0 100%, 18% 25%, 38% 75%, 58% 12%, 78% 60%, 100% 100%)" }}
    />
    <div
      className="absolute bottom-10 h-[32%] w-full bg-[#111834]"
      style={{ clipPath: "polygon(0 100%, 30% 40%, 55% 85%, 75% 30%, 100% 100%)" }}
    />
    <div className="absolute bottom-0 h-10 w-full bg-gradient-to-t from-[#00E5FF]/25 to-transparent" />
    <div className="relative flex h-full flex-col p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-md bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2]" />
          <Bar className="h-1.5 w-12 bg-white/30" />
        </div>
        <div className="flex items-center gap-2">
          <Bar className="h-1 w-8 bg-white/20" />
          <Bar className="h-1 w-8 bg-white/20" />
          <div className="h-3.5 w-12 rounded-full bg-white/90" />
        </div>
      </div>
      <div className="mt-6 space-y-2">
        <Bar className="h-4 w-3/4 bg-white/80" />
        <Bar className="h-4 w-1/2 bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]" />
        <Bar className="h-1.5 w-2/5 bg-white/25" />
        <div className="pt-2">
          <div className="h-4 w-20 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]" />
        </div>
      </div>
      <div className="mt-auto grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5 rounded-lg border border-white/10 bg-white/[0.06] p-2 backdrop-blur-sm">
            <div className="h-6 rounded-md bg-gradient-to-br from-[#16224A] to-[#0B1530]" />
            <Bar className="h-1 w-3/4 bg-white/25" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const PizzariaScreen = () => (
  <div className="relative flex h-full flex-col p-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span
          className="h-6 w-6 rounded-full"
          style={{ background: "conic-gradient(#F59E0B 0 25%, #B45309 25% 50%, #F59E0B 50% 75%, #B45309 75% 100%)" }}
        />
        <Bar className="h-2 w-14 bg-white/30" />
      </div>
      <div className="h-4 w-16 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2]" />
    </div>
    <div className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-3">
      <div
        className="relative h-16 w-16 shrink-0 rounded-full"
        style={{ background: "radial-gradient(circle at 35% 35%, #FBBF24, #B45309 70%)" }}
      >
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className="absolute h-2 w-2 rounded-full bg-[#7C2D12]"
            style={{
              left: `${18 + ((i * 23) % 55)}%`,
              top: `${15 + ((i * 31) % 60)}%`,
            }}
          />
        ))}
      </div>
      <div className="flex-1 space-y-2">
        <Bar className="h-2.5 w-3/4 bg-white/70" />
        <Bar className="h-1.5 w-full bg-white/15" />
        <Bar className="h-1.5 w-2/3 bg-white/15" />
        <div className="flex items-center justify-between pt-1">
          <div className="h-3.5 w-12 rounded-full bg-[#F59E0B]/80" />
          <Bar className="h-1.5 w-8 bg-white/25" />
        </div>
      </div>
    </div>
    <div className="mt-3 flex-1 space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
          <span
            className="h-8 w-8 shrink-0 rounded-full"
            style={{
              background: `radial-gradient(circle at 35% 35%, ${i === 1 ? "#00E5FF" : "#FBBF24"}, ${i === 1 ? "#1D4ED8" : "#B45309"} 75%)`,
            }}
          />
          <div className="flex-1 space-y-1.5">
            <Bar className="h-1.5 w-2/3 bg-white/35" />
            <Bar className="h-1 w-1/2 bg-white/12" />
          </div>
          <div className="h-3 w-10 rounded-full border border-white/20" />
        </div>
      ))}
    </div>
  </div>
);

export const DashboardScreen = () => (
  <div className="flex h-full">
    <div className="hidden w-11 flex-col items-center gap-3 border-r border-white/10 py-3.5 sm:flex">
      <span className="h-4 w-4 rounded-md bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2]" />
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === 0 ? "bg-[#00E5FF]" : "bg-white/20"}`} />
      ))}
    </div>
    <div className="min-w-0 flex-1 p-3.5">
      <div className="flex items-center justify-between">
        <div className="space-y-1.5">
          <Bar className="h-2.5 w-24 bg-white/30" />
          <Bar className="h-1.5 w-16 bg-white/12" />
        </div>
        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-[#00E5FF]/60 to-[#8A2BE2]/60" />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-1.5">
        {["Vendas", "Clientes", "Pedidos", "Receita"].map((k) => (
          <div key={k} className="rounded-lg border border-white/10 bg-white/[0.04] p-2">
            <span className="block text-[6px] uppercase tracking-wider text-white/40">{k}</span>
            <Bar className="mt-1.5 h-2 w-4/5 bg-gradient-to-r from-[#00E5FF]/80 to-[#8A2BE2]/80" />
          </div>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1.5">
        <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
          <Bar className="h-1.5 w-16 bg-white/20" />
          <div className="mt-2 flex h-16 items-end gap-1">
            {[42, 68, 50, 85, 62, 78, 55, 92, 70].map((h, i) => (
              <div
                key={i}
                aria-hidden="true"
                style={{ height: `${h}%` }}
                className="flex-1 rounded-sm bg-gradient-to-t from-[#8A2BE2]/60 to-[#00E5FF]/75"
              />
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] p-2">
          <div
            className="relative h-14 w-14 rounded-full"
            style={{
              background:
                "conic-gradient(#00E5FF 0 42%, #8A2BE2 42% 74%, rgba(255,255,255,0.12) 74% 100%)",
            }}
          >
            <div className="absolute inset-2.5 rounded-full bg-[#0A0A14]" />
          </div>
        </div>
      </div>
      <div className="mt-2 space-y-1.5">
        {[0, 1].map((i) => (
          <div key={i} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2 py-1.5">
            <span className="h-3.5 w-3.5 rounded-full bg-white/10" />
            <Bar className="h-1.5 w-1/3 bg-white/20" />
            <Bar className="ml-auto h-1.5 w-10 bg-[#00E5FF]/40" />
          </div>
        ))}
      </div>
    </div>
  </div>
);
